import { useState, type ComponentType } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
  rectIntersection,
  closestCenter,
  MeasuringStrategy,
  type CollisionDetection,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { X, GripVertical, LayoutGrid, Check } from "lucide-react";

/* ── Shared types ─────────────────────────────────────────────────────── */
export interface ConfigCard {
  key: string;
  group: string;
  label: string;
  sub: string;
  tooltip: string;
  display: string;
  Icon: ComponentType<{ className?: string }>;
  iconBox: string;
  border: string;
  valueColor: string;
  accent: string;
}
export interface ConfigGroup {
  group: string;
  title: string;
  desc?: string;
  cards: ConfigCard[];
}
export interface KeyMetricsConfig {
  columns: number;
  hidden: string[];
  order: Record<string, string[]>;
}

const COLUMN_OPTIONS = [2, 4, 5] as const;

/* Forgiving collision detection: prefer whatever the pointer is over, then fall
   back to rectangle overlap, then the nearest zone — so a drop always lands
   somewhere sensible even if the pointer isn't perfectly inside a drop box. */
const collisionDetection: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length) return pointer;
  const rect = rectIntersection(args);
  if (rect.length) return rect;
  return closestCenter(args);
};

/* ── Presentational card chip (no dnd hooks — safe for the DragOverlay) ── */
function CardChipView({ card, dim = false }: { card: ConfigCard; dim?: boolean }) {
  return (
    <div
      className={`flex cursor-grab items-center gap-2 rounded-lg border p-2.5 active:cursor-grabbing ${card.border} ${
        dim ? "opacity-40" : ""
      }`}
    >
      <GripVertical className="size-3.5 shrink-0 text-[#94a3b8] dark:text-white/30" />
      <div className={`flex size-6 shrink-0 items-center justify-center rounded-md ${card.iconBox}`}>
        <card.Icon className="size-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-[#0f172a] dark:text-white/90">{card.label}</p>
      </div>
      <span className="shrink-0 text-sm font-bold tabular-nums text-[#0f172a] dark:text-white">{card.display}</span>
    </div>
  );
}

/* ── Draggable wrapper ────────────────────────────────────────────────── */
function CardChip({ card }: { card: ConfigCard }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.key });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}>
      <CardChipView card={card} dim={isDragging} />
    </div>
  );
}

/* ── A droppable container ────────────────────────────────────────────── */
function DropZone({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`${className ?? ""} transition-colors ${
        isOver ? "border-indigo-400 bg-indigo-50/60 dark:border-indigo-400/60 dark:bg-indigo-500/[0.08]" : ""
      }`}
    >
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   KEY METRICS CONFIG MODAL
   Left  = available (hidden) cards, grouped by heading.
   Right = "on your dashboard" — one drop box per heading; drag cards in/out.
   Plus a cards-per-row selector. Emits the new config on Save.
   ══════════════════════════════════════════════════════════════════════ */
export function KeyMetricsConfigModal({
  groups,
  config,
  onApply,
  onClose,
}: {
  groups: ConfigGroup[];
  config: KeyMetricsConfig;
  onApply: (c: KeyMetricsConfig) => void;
  onClose: () => void;
}) {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(config.hidden));
  const [order, setOrder] = useState<Record<string, string[]>>(() => ({ ...config.order }));
  const [columns, setColumns] = useState<number>(config.columns || 4);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const cardByKey = (k: string) => groups.flatMap((g) => g.cards).find((c) => c.key === k) ?? null;

  /* Visible (dashboard) cards for a group, in saved order then any extras. */
  const visibleCards = (g: ConfigGroup) => {
    const ord = order[g.group] ?? [];
    const inOrder = ord.map((k) => g.cards.find((c) => c.key === k)).filter(Boolean) as ConfigCard[];
    const rest = g.cards.filter((c) => !hidden.has(c.key) && !ord.includes(c.key));
    return [...inOrder.filter((c) => !hidden.has(c.key)), ...rest];
  };
  /* Available (hidden) cards for a group. */
  const availableCards = (g: ConfigGroup) => g.cards.filter((c) => hidden.has(c.key));

  const totalAvailable = groups.reduce((n, g) => n + availableCards(g).length, 0);

  const onDragStart = (e: DragStartEvent) => setActiveKey(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveKey(null);
    const key = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId) return;
    const card = cardByKey(key);
    if (!card) return;

    if (overId === "pool") {
      // hide the card
      setHidden((prev) => new Set(prev).add(key));
      setOrder((prev) => ({ ...prev, [card.group]: (prev[card.group] ?? []).filter((k) => k !== key) }));
    } else if (overId.startsWith("grp:")) {
      // show it in its own group (appended)
      setHidden((prev) => {
        const n = new Set(prev);
        n.delete(key);
        return n;
      });
      setOrder((prev) => {
        const list = prev[card.group] ?? [];
        return list.includes(key) ? prev : { ...prev, [card.group]: [...list, key] };
      });
    }
  };

  const activeCard = activeKey ? cardByKey(activeKey) : null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-md" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white shadow-2xl dark:border-white/[0.08] dark:bg-[#0f0e1f]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-[#e2e8f0] px-6 pb-4 pt-5 dark:border-white/[0.06]">
            <div className="min-w-0">
              <h2 className="text-base font-bold text-[#0f172a] dark:text-white">Configure Key Metrics</h2>
              <p className="mt-0.5 text-xs text-[#64748b] dark:text-white/55">
                Drag cards between panels to choose what shows, and pick how many cards sit in a row.
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f1f5f9] text-[#64748b] transition-colors hover:bg-[#e2e8f0] dark:bg-white/[0.06] dark:text-white/40 dark:hover:bg-white/[0.12]"
            >
              <X className="size-4" />
            </button>
          </div>

          {/* Cards-per-row selector */}
          <div className="flex items-center gap-3 border-b border-[#e2e8f0] px-6 py-3 dark:border-white/[0.06]">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-[#475569] dark:text-white/60">
              <LayoutGrid className="size-3.5" /> Cards per row
            </span>
            <div className="inline-flex items-center gap-1 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-1 dark:border-white/[0.08] dark:bg-white/[0.03]">
              {COLUMN_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => setColumns(n)}
                  className={`rounded-md px-3 py-1 text-sm font-semibold transition-colors ${
                    columns === n
                      ? "bg-white text-indigo-600 shadow-sm dark:bg-indigo-600 dark:text-white"
                      : "text-[#64748b] hover:text-[#0f172a] dark:text-white/55 dark:hover:text-white"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Dual-pane DnD body */}
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            autoScroll={{ threshold: { x: 0, y: 0.2 }, acceleration: 12 }}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          >
            <div className="grid flex-1 grid-cols-1 gap-4 overflow-y-auto overscroll-contain p-6 md:grid-cols-2">
              {/* LEFT — available cards (drop here to hide) */}
              <DropZone
                id="pool"
                className="flex min-h-[300px] flex-col rounded-xl border border-dashed border-[#e2e8f0] bg-[#fafbfc] p-3 dark:border-white/[0.1] dark:bg-white/[0.02]"
              >
                <p className="mb-3 px-1 text-[13px] font-bold tracking-wide text-[#334155] dark:text-white/75">
                  Available Cards
                </p>
                <div className="flex-1 space-y-4">
                  {totalAvailable === 0 ? (
                    <p className="py-8 text-center text-xs text-[#94a3b8] dark:text-white/40">
                      All cards are on your dashboard. Drag any here to hide it.
                    </p>
                  ) : (
                    groups.map((g) => {
                      const cards = availableCards(g);
                      if (!cards.length) return null;
                      return (
                        <div key={g.group}>
                          <p className="mb-2 px-1 text-[13.5px] font-semibold tracking-wide text-[#475569] dark:text-white/55">
                            {g.title}
                          </p>
                          <div className="space-y-2">
                            {cards.map((c) => (
                              <CardChip key={c.key} card={c} />
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </DropZone>

              {/* RIGHT — on your dashboard (per-heading drop boxes) */}
              <div className="flex flex-col rounded-xl border border-[#e2e8f0] bg-white p-3 dark:border-white/[0.08] dark:bg-[#131320]">
                <p className="mb-3 px-1 text-[13px] font-bold tracking-wide text-[#334155] dark:text-white/75">
                  On Your Dashboard
                </p>
                <div className="flex-1 space-y-4">
                  {groups.map((g) => {
                    const cards = visibleCards(g);
                    return (
                      <div key={g.group}>
                        <p className="mb-2 px-1 text-[13.5px] font-semibold tracking-wide text-[#475569] dark:text-white/55">
                          {g.title}
                        </p>
                        <DropZone
                          id={`grp:${g.group}`}
                          className="min-h-[56px] space-y-2 rounded-lg border border-dashed border-[#e2e8f0] p-2 dark:border-white/[0.1]"
                        >
                          {cards.length === 0 ? (
                            <p className="py-3 text-center text-[11px] text-[#94a3b8] dark:text-white/35">
                              Drag cards here
                            </p>
                          ) : (
                            cards.map((c) => <CardChip key={c.key} card={c} />)
                          )}
                        </DropZone>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <DragOverlay>
              {activeCard ? (
                <div className="w-64 cursor-grabbing shadow-xl">
                  <CardChipView card={activeCard} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-[#e2e8f0] px-6 py-4 dark:border-white/[0.06]">
            <button
              onClick={onClose}
              className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-semibold text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:border-white/[0.1] dark:text-white/70 dark:hover:bg-white/[0.05]"
            >
              Cancel
            </button>
            <button
              onClick={() => onApply({ columns, hidden: [...hidden], order })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
            >
              <Check className="size-4" /> Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
